import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import type { Pagination } from "~/lib/api";
import { formatCount } from "~/lib/format";

const PAGE_SIZES = [25, 50, 100, 200] as const;

/**
 * Server-side paging against the {limit, offset, total} envelope.
 *
 * Offset rather than a page number because that is what the API takes; deriving
 * one from the other in two places is how a table ends up off by one page.
 *
 * `estimated` is false on every admin list today -- migration 013 indexed the
 * filters, so an exact count is affordable -- but the flag exists for the day it
 * is not, and a count that has quietly become a guess must not read as exact.
 */
export function PaginationBar({
  pagination,
  onChange,
  disabled,
}: {
  pagination: Pagination;
  onChange: (next: { limit: number; offset: number }) => void;
  disabled?: boolean;
}) {
  const { limit, offset, total, estimated } = pagination;

  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + limit, total);
  const hasPrevious = offset > 0;
  const hasNext = offset + limit < total;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t px-2 py-2.5 text-sm">
      <p className="text-muted-foreground">
        {total === 0 ? (
          "No results"
        ) : (
          <>
            {formatCount(first)}–{formatCount(last)} of {estimated ? "about " : ""}
            {formatCount(total)}
          </>
        )}
      </p>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Per page</span>
          <Select
            value={String(limit)}
            onValueChange={(value) =>
              // Back to the first page: holding an offset of 500 while shrinking
              // the page size lands somewhere nobody asked for.
              onChange({ limit: Number(value), offset: 0 })
            }
          >
            <SelectTrigger size="sm" className="w-[4.5rem]" disabled={disabled}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={disabled || !hasPrevious}
            onClick={() => onChange({ limit, offset: Math.max(0, offset - limit) })}
          >
            <ChevronLeftIcon className="size-4" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled || !hasNext}
            onClick={() => onChange({ limit, offset: offset + limit })}
          >
            Next
            <ChevronRightIcon className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
